import React, { useState, useEffect, useMemo } from 'react';
import { Table } from '../ui/Table';
import { SearchFilter } from '../ui/SearchFilter';
import { Plus, Edit2, Trash2, Tag as TagIcon, UploadCloud } from 'lucide-react';
import {
  adminListFoodTags,
  createFoodTag,
  updateFoodTag,
  deleteFoodTag,
} from '../../services/resortAdminApi';
import { useAuth } from '../../context/AuthContext';

interface TagRow {
  name: string;
  label: string;
  is_active: number;
  is_restaurant_tag: number;
  is_grocery_tag: number;
  available_as_search_filter: number;
  icon_svg?: string | null;
}

interface FormState {
  label: string;
  is_active: boolean;
  is_restaurant_tag: boolean;
  is_grocery_tag: boolean;
  available_as_search_filter: boolean;
  icon_svg: string | null;
}

const emptyForm: FormState = {
  label: '',
  is_active: true,
  is_restaurant_tag: false,
  is_grocery_tag: false,
  available_as_search_filter: false,
  icon_svg: null,
};

// Render SVG markup as an <img> data URI so no embedded script can ever run.
function svgDataUri(svg?: string | null): string {
  if (!svg) return '';
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function IconPreview({ svg, size = 28 }: { svg?: string | null; size?: number }) {
  if (!svg) {
    return (
      <div
        className="rounded bg-gray-100 flex items-center justify-center text-gray-400"
        style={{ width: size, height: size }}
        title="No icon"
      >
        <TagIcon className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={svgDataUri(svg)}
      alt="icon"
      className="rounded border border-gray-200 bg-white object-contain p-0.5"
      style={{ width: size, height: size }}
    />
  );
}

interface TagsManagerProps {
  title: string;
  subtitle: string;
}

export const TagsManager: React.FC<TagsManagerProps> = ({ title, subtitle }) => {
  const { user, isReadOnly } = useAuth();
  const canWrite = (user?.role === 'Resort Super Admin' || user?.role === 'Editor') && !isReadOnly;

  const [searchTerm, setSearchTerm] = useState('');
  const [tags, setTags] = useState<TagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editing, setEditing] = useState<TagRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; tag: TagRow | null }>({
    show: false,
    tag: null,
  });
  const [deleting, setDeleting] = useState(false);

  const loadTags = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await adminListFoodTags();
      const normalized: TagRow[] = (Array.isArray(rows) ? rows : []).map((r) => ({
        name: r.name,
        label: r.food_tag_name,
        is_active: Number(r.is_active) || 0,
        is_restaurant_tag: Number(r.is_restaurant_tag) || 0,
        is_grocery_tag: Number(r.is_grocery_tag) || 0,
        available_as_search_filter: Number(r.available_as_search_filter) || 0,
        icon_svg: r.icon_svg ?? null,
      }));
      setTags(normalized);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => t.label.toLowerCase().includes(q));
  }, [tags, searchTerm]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowFormModal(true);
  };

  const openEdit = (tag: TagRow) => {
    setEditing(tag);
    setForm({
      label: tag.label,
      is_active: !!tag.is_active,
      is_restaurant_tag: !!tag.is_restaurant_tag,
      is_grocery_tag: !!tag.is_grocery_tag,
      available_as_search_filter: !!tag.available_as_search_filter,
      icon_svg: tag.icon_svg ?? null,
    });
    setError('');
    setShowFormModal(true);
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (!isSvg) {
      setError('Please upload an .svg file');
      return;
    }
    if (file.size > 100_000) {
      setError('SVG icon is too large (max 100 KB)');
      return;
    }
    try {
      const text = await file.text();
      if (!/<svg[\s\S]*<\/svg>/i.test(text)) {
        setError('The file does not look like a valid SVG');
        return;
      }
      setError('');
      setForm((f) => ({ ...f, icon_svg: text }));
    } catch {
      setError('Failed to read SVG file');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      if (editing) {
        await updateFoodTag({
          tag_id: editing.name,
          food_tag_name: form.label,
          is_restaurant_tag: form.is_restaurant_tag ? 1 : 0,
          is_grocery_tag: form.is_grocery_tag ? 1 : 0,
          is_active: form.is_active ? 1 : 0,
          available_as_search_filter: form.available_as_search_filter ? 1 : 0,
          icon_svg: form.icon_svg ?? '',
          clear_icon: !form.icon_svg,
        });
        setSuccess(`Tag "${form.label}" updated`);
      } else {
        await createFoodTag({
          food_tag_name: form.label,
          is_restaurant_tag: form.is_restaurant_tag ? 1 : 0,
          is_grocery_tag: form.is_grocery_tag ? 1 : 0,
          is_active: form.is_active ? 1 : 0,
          available_as_search_filter: form.available_as_search_filter ? 1 : 0,
          icon_svg: form.icon_svg ?? undefined,
        });
        setSuccess(`Tag "${form.label}" created`);
      }
      setShowFormModal(false);
      setForm(emptyForm);
      setEditing(null);
      loadTags();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteModal.tag) return;
    setDeleting(true);
    setError('');
    try {
      await deleteFoodTag(showDeleteModal.tag.name);
      setSuccess(`Tag "${showDeleteModal.tag.label}" deleted`);
      setShowDeleteModal({ show: false, tag: null });
      loadTags();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to delete tag');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'icon_svg',
      header: 'Icon',
      render: (_: any, row: TagRow) => <IconPreview svg={row.icon_svg} />,
    },
    {
      key: 'label',
      header: 'Name',
      render: (_: any, row: TagRow) => <span className="font-medium text-gray-800">{row.label}</span>,
    },
    {
      key: 'is_restaurant_tag',
      header: 'Type',
      render: (_: any, row: TagRow) =>
        row.is_restaurant_tag ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Restaurant
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            Dish
          </span>
        ),
    },
    {
      key: 'is_grocery_tag',
      header: 'Grocery',
      render: (_: any, row: TagRow) =>
        row.is_grocery_tag ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            Grocery
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'available_as_search_filter',
      header: 'Search filter',
      render: (_: any, row: TagRow) =>
        row.available_as_search_filter ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Quick filter
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_: any, row: TagRow) =>
        row.is_active ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
            Inactive
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: TagRow) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openEdit(row)}
            disabled={!canWrite}
            className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowDeleteModal({ show: true, tag: row })}
            disabled={!canWrite}
            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>
          <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
        </div>
        {canWrite && (
          <button
            onClick={openCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Tag
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Search */}
      <SearchFilter
        initialValue={searchTerm}
        onSearch={setSearchTerm}
        placeholder="Search tags..."
      />

      {/* Table */}
      <Table columns={columns} data={filtered} isLoading={loading} emptyMessage="No tags found" />

      {/* Create / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{editing ? 'Edit Tag' : 'Create Tag'}</h2>
              <button
                onClick={() => {
                  setShowFormModal(false);
                  setForm(emptyForm);
                  setEditing(null);
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon (SVG)</label>
                <div className="flex items-center space-x-3">
                  <IconPreview svg={form.icon_svg} size={40} />
                  <label className="relative cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                    <UploadCloud className="h-4 w-4 mr-2" />
                    {form.icon_svg ? 'Replace SVG' : 'Upload SVG'}
                    <input type="file" className="sr-only" accept=".svg,image/svg+xml" onChange={handleIconUpload} />
                  </label>
                  {form.icon_svg && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, icon_svg: null })}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">SVG only, up to 100 KB. Shown next to the tag in the client app.</p>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="tag-active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded focus:ring-alps-primary"
                />
                <label htmlFor="tag-active" className="text-sm text-gray-700">
                  Active <span className="text-gray-400">(shown in dropdowns)</span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="tag-restaurant"
                  type="checkbox"
                  checked={form.is_restaurant_tag}
                  onChange={(e) => setForm({ ...form, is_restaurant_tag: e.target.checked })}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded focus:ring-alps-primary"
                />
                <label htmlFor="tag-restaurant" className="text-sm text-gray-700">
                  Restaurant-level tag <span className="text-gray-400">(tags a whole restaurant, not a dish)</span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="tag-grocery"
                  type="checkbox"
                  checked={form.is_grocery_tag}
                  onChange={(e) => setForm({ ...form, is_grocery_tag: e.target.checked })}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded focus:ring-alps-primary"
                />
                <label htmlFor="tag-grocery" className="text-sm text-gray-700">
                  Grocery food tag{' '}
                  <span className="text-gray-400">(available for groceries and grocery products)</span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  id="tag-search-filter"
                  type="checkbox"
                  checked={form.available_as_search_filter}
                  onChange={(e) => setForm({ ...form, available_as_search_filter: e.target.checked })}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded focus:ring-alps-primary"
                />
                <label htmlFor="tag-search-filter" className="text-sm text-gray-700">
                  Available as search filter{' '}
                  <span className="text-gray-400">(quick-filter chip on the client search screen)</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFormModal(false);
                    setForm(emptyForm);
                    setEditing(null);
                    setError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !form.label.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal.show && showDeleteModal.tag && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-red-600">Delete Tag</h2>
              <button
                onClick={() => setShowDeleteModal({ show: false, tag: null })}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Delete the tag "{showDeleteModal.tag.label}"? It will be removed from every product that
              uses it (the products themselves are kept). This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal({ show: false, tag: null })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
